import React from "react";

import {
  faBriefcase,
  faChevronDown,
  faEdit,
  faExclamationCircle,
  faPlus,
  faTable,
} from "@fortawesome/pro-regular-svg-icons";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { groupBy, times } from "lodash-es";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import {
  ResourceCard,
  ResourceCardSkeleton,
  ResourceCardsContainer,
} from "src/base-components/ResourceCard";
import {
  ResourceStatusPill,
  ResourceStatusPillContainer,
} from "src/base-components/ResourceStatusPill";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { CaseTemplateModal } from "src/cases/templates/CaseTemplateModal";
import { getTemplateIcon } from "src/cases/templates/utils";
import {
  CaseTemplateVersionPublishState,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Modal, useModal } from "src/design-system/Modal";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  DashboardPageParams,
  getUrlToCaseQueuePage,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";
import { pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const CaseTemplatesList: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const {
    data: caseTemplates,
    isError,
    isLoading,
    refetch,
    isFetching,
  } = useCaseTemplatesWithVersions();
  const navigate = useNavigate();
  const {
    isOpen,
    openModal,
    closeModal,
    data: templateIdToEdit,
  } = useModal<string>();

  if (!isLoading && isError) {
    return (
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
            disabled: isFetching,
          }}
          dataLoc="case-templates-error-state"
          description="We're having trouble loading case templates. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading case templates"
          icon={faExclamationCircle}
          variant="error"
        />
      </WhitePane>
    );
  }

  if (!isLoading && (!caseTemplates || caseTemplates.length === 0)) {
    return (
      <WhitePane>
        <EmptyState
          dataLoc="case-templates-empty-state"
          description="There are no templates found in this workspace yet."
          headline="No templates found"
          icon={faBriefcase}
        />
      </WhitePane>
    );
  }

  return (
    <>
      <ResourceCardsContainer>
        {isLoading
          ? times(3, (i) => <ResourceCardSkeleton key={i} />)
          : caseTemplates?.map((template) => (
              <ResourceCard
                key={template.id}
                dataLoc={`template-card-${template.id}`}
                description={template.attributes.description}
                title={
                  <div className="flex items-start gap-1.5">
                    <Icon
                      color="text-gray-600"
                      icon={
                        getTemplateIcon(template.attributes.icon) ?? faTable
                      }
                      size="xs"
                    />
                    <div className="line-clamp-2">
                      {template.attributes.template_name}
                    </div>
                  </div>
                }
                onClick={() => {
                  navigate(
                    getUrlToCaseTemplateVersionsPage(orgId, wsId, template.id),
                  );
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <TemplateVersionsPills versions={template.versions} />
                  <EllipsisOptionsDropdown
                    buttonClassName="opacity-0 group-hover/resource-card:opacity-100 w-6 h-6"
                    dataLoc="template-actions"
                    elements={[
                      {
                        key: "Edit details",
                        icon: faEdit,
                        action: () => openModal(template.id),
                      },
                    ]}
                    placement="bottom start"
                  />
                </div>
              </ResourceCard>
            ))}
      </ResourceCardsContainer>
      <CaseTemplateModal
        open={isOpen}
        templateId={templateIdToEdit}
        onClose={closeModal}
      />
    </>
  );
};

type TemplateVersionsPillsProps = {
  versions?: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

const TemplateVersionsPills: React.FC<TemplateVersionsPillsProps> = ({
  versions,
}) => {
  const versionsByState = groupBy(versions, "attributes.publish_state");

  return (
    <ResourceStatusPillContainer>
      {Object.entries(versionsByState).map(([state, versions]) => (
        <ResourceStatusPill
          key={state}
          color={
            state === CaseTemplateVersionPublishState.PUBLISHED
              ? "green"
              : "yellow"
          }
          tooltip={{
            title: pluralize(
              versions.length,
              state === CaseTemplateVersionPublishState.PUBLISHED
                ? "published version"
                : "draft version",
            ),
          }}
          value={versions.length}
        />
      ))}
    </ResourceStatusPillContainer>
  );
};

const CreateFromTemplateModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Create template from existing template</Modal.Header>
      <Modal.Content>
        <div className="py-8">
          <p className="font-inter-normal-16px text-gray-600">
            Will be implemented soon
          </p>
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        }
      />
    </Modal>
  );
};

export const CaseTemplatesPage: React.FC = () => {
  useDocumentTitle(["Case Templates"]);
  const { search } = useLocation();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { caseTemplates } = useCapabilities();
  const isTemplateCopyEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.templateCopy,
  );

  const canCreateTemplate = caseTemplates.canCreate && isTemplateCopyEnabled;

  const createModal = useModal();
  const fromTemplateModal = useModal();

  return (
    <div className="flex h-full flex-col" data-loc="case-templates-page">
      <PageHeader
        breadcrumbs={[
          {
            key: "cases-page",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="cases-page"
                href={getUrlToCaseQueuePage({
                  wsId,
                  orgId,
                })}
                label="Cases"
                hideIcon
              />
            ),
          },
          {
            key: "case-templates",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="case-templates"
                href={`.${search}`}
                label="Case Templates"
                active
              />
            ),
          },
        ]}
        {...(canCreateTemplate && {
          sideContent: (
            <OptionsDropdown
              elements={[
                {
                  key: "From scratch",
                  action: createModal.openModal,
                },
                {
                  key: "From a template",
                  action: fromTemplateModal.openModal,
                },
              ]}
            >
              <Button
                dataLoc="create-case-template"
                iconLeft={faPlus}
                iconRight={faChevronDown}
                size="base"
                variant="primary"
              >
                Create case template
              </Button>
            </OptionsDropdown>
          ),
        })}
      />

      <div className="decideScrollbar h-full min-w-[300px] overflow-hidden p-5">
        <CaseTemplatesList />
      </div>

      <CaseTemplateModal
        open={createModal.isOpen}
        onClose={createModal.closeModal}
      />
      <CreateFromTemplateModal
        open={fromTemplateModal.isOpen}
        onClose={fromTemplateModal.closeModal}
      />
    </div>
  );
};
